import React, { useState, useEffect } from 'react';
import { Row, Col, Badge, Card, CardBody, CardHeader } from 'reactstrap';
import { LayoutGrid, GripVertical, Minus, Split, ChefHat } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSpaceStore } from '../../../stores/Assets/space';
import { useSiteStore } from '../../../stores/Assets/site';

const THEME_COLOR = '#c59253';

const SpaceLinking = ({ state, updateState }) => {
    const { sites } = useSiteStore();
    const { spaces, getSpaces } = useSpaceStore();
    const [unassignedSpaces, setUnassignedSpaces] = useState([]);
    const [dragOverStationId, setDragOverStationId] = useState(null);
    const [selectedItems, setSelectedItems] = useState([]);

    const siteId = sites?.[0]?.site_id;

    useEffect(() => {
        if (siteId) {
            getSpaces(siteId, { page: 1, perPage: 100 });
        }
    }, [siteId]);

    useEffect(() => {
        if (spaces) {
            const linkedSpaceIds = Object.values(state.spaceLinks).flat();
            setUnassignedSpaces(spaces.filter(s => !linkedSpaceIds.includes(s.space_id)));
        }
    }, [spaces, state.spaceLinks]);

    const toggleSelection = (id) => {
        setSelectedItems(prev => {
            if (prev.includes(id)) return prev.filter(i => i !== id);
            return [...prev, id];
        });
    };

    const handleDragStart = (e, ids) => {
        e.dataTransfer.setData('spaceIds', JSON.stringify(ids));
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDrop = (e, stationId) => {
        e.preventDefault();
        setDragOverStationId(null);
        const data = e.dataTransfer.getData('spaceIds');
        if (!data) return;

        const droppedIds = JSON.parse(data);
        const currentLinks = { ...state.spaceLinks };

        currentLinks[stationId] = [...new Set([...(currentLinks[stationId] || []), ...droppedIds])];

        Object.keys(currentLinks).forEach(sid => {
            if (sid != stationId) {
                currentLinks[sid] = currentLinks[sid].filter(id => !droppedIds.includes(id));
            }
        });

        updateState({ spaceLinks: currentLinks });
        setSelectedItems([]);
    };

    const unassignSpace = (stationId, spaceId) => {
        const currentLinks = { ...state.spaceLinks };
        currentLinks[stationId] = currentLinks[stationId].filter(id => id !== spaceId);
        updateState({ spaceLinks: currentLinks });
    };

    return (
        <div className="container-fluid py-4" style={{ height: 'calc(100vh - 180px)' }}>
            <Row className="h-100 g-4">
                <Col lg={3} className="h-100">
                    <Card className="h-100 border-0 shadow-sm rounded-4 overflow-hidden">
                        <CardHeader className="bg-white p-4 border-bottom">
                            <div className="d-flex align-items-center justify-content-between">
                                <h5 className="mb-0 fw-bold">Physical Spaces</h5>
                                <Badge color="soft-primary">{unassignedSpaces.length}</Badge>
                            </div>
                            <p className="text-muted small mb-0 mt-2">Drag spaces to preparation stations.</p>
                        </CardHeader>
                        <CardBody className="p-3 overflow-auto">
                            <AnimatePresence mode="popLayout">
                                {unassignedSpaces.map(space => {
                                    const isSelected = selectedItems.includes(space.space_id);
                                    return (
                                        <motion.div
                                            key={space.space_id}
                                            layout
                                            draggable
                                            onDragStart={(e) => {
                                                const ids = isSelected ? selectedItems : [space.space_id];
                                                if (!isSelected) toggleSelection(space.space_id);
                                                handleDragStart(e, ids);
                                            }}
                                            onClick={() => toggleSelection(space.space_id)}
                                            className={`p-3 rounded-3 border mb-2 d-flex align-items-center gap-2 cursor-grab transition-all ${isSelected ? 'border-primary bg-soft-primary' : 'bg-white hover-light'}`}
                                        >
                                            <GripVertical size={16} className="text-muted" />
                                            <span className="small fw-bold text-truncate">{space.name}</span>
                                        </motion.div>
                                    );
                                })}
                            </AnimatePresence>
                            {unassignedSpaces.length === 0 && (
                                <div className="text-center py-5 opacity-50 border border-dashed rounded-3">
                                    <LayoutGrid size={32} className="mb-2" />
                                    <p className="small mb-0">All spaces assigned</p>
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </Col>

                <Col lg={9} className="h-100 overflow-auto pe-3">
                    <div className="mb-4">
                        <h4 className="fw-bold mb-1">Link Spaces to Stations</h4>
                        <p className="text-muted">Define which kitchen handles orders from specific physical areas.</p>
                    </div>

                    <Row className="g-4">
                        {state.stations.map((station) => (
                            <Col key={station.id} md={6} xl={4}>
                                <div
                                    onDragOver={(e) => { e.preventDefault(); setDragOverStationId(station.id); }}
                                    onDragLeave={() => setDragOverStationId(null)}
                                    onDrop={(e) => handleDrop(e, station.id)}
                                    className={`bg-white rounded-4 shadow-sm border h-100 overflow-hidden d-flex flex-column transition-all ${dragOverStationId === station.id ? 'border-primary ring-2 ring-primary bg-soft-primary' : ''}`}
                                    style={{
                                        minHeight: '200px',
                                        transform: dragOverStationId === station.id ? 'scale(1.02)' : 'none'
                                    }}
                                >
                                    <div className="p-3 bg-light border-bottom d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="p-1 bg-white rounded-2 border">
                                                <ChefHat size={16} style={{ color: THEME_COLOR }} />
                                            </div>
                                            <h6 className="fw-bold mb-0">{station.name}</h6>
                                        </div>
                                        <Badge color="soft-primary">
                                            {(state.spaceLinks[station.id] || []).length} Spaces
                                        </Badge>
                                    </div>

                                    <div className="p-3 flex-grow-1">
                                        <div className="d-flex flex-column gap-2">
                                            {(state.spaceLinks[station.id] || []).map(spaceId => {
                                                const space = spaces.find(s => s.space_id === spaceId);
                                                return (
                                                    <div key={spaceId} className="bg-white border rounded-3 p-2 px-3 d-flex justify-content-between align-items-center shadow-sm">
                                                        <span className="small fw-bold text-truncate">{space?.name}</span>
                                                        <button
                                                            onClick={() => unassignSpace(station.id, spaceId)}
                                                            className="btn btn-link p-0 text-danger border-0 hov-scale"
                                                        >
                                                            <Minus size={14} />
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                            {(!state.spaceLinks[station.id] || state.spaceLinks[station.id].length === 0) && (
                                                <div className="text-center py-4 border-2 border-dashed rounded-3 opacity-25 d-flex flex-column align-items-center gap-2">
                                                    <Split size={24} />
                                                    <span style={{ fontSize: '10px' }}>Drop Spaces Here</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Col>
            </Row>

            <style>{`
                .bg-soft-primary { background-color: rgba(197, 146, 83, 0.1) !important; }
                .hover-light:hover { background-color: #f8f9fa !important; }
                .cursor-grab { cursor: grab; }
                .cursor-grab:active { cursor: grabbing; }
                .hov-scale:hover { transform: scale(1.2); }
                .ring-2 { box-shadow: 0 0 0 2px #c59253; }
            `}</style>
        </div>
    );
};

export default SpaceLinking;
